import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useHyperlinkSelection } from 'components/WorkspaceEditor/features/hyperlinkSelection'
import { findNodeById } from 'components/WorkspaceEditor/features/promoteDemote/utils'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles/useTreeViewStyles'
import { DataNode } from 'components/WorkspaceEditor/types'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { useMemo } from 'react'
import { NodeOrder } from './NodeOrder'
import { PlainText } from './PlainText/PlainText'
import { SelectionOption } from './SelectionOption'

interface HyperlinkProps {
    contentItem: Partial<TextContentElement>
    isKeyedHyperlink: boolean
    itemOrder?: string
    inactive?: boolean
    disabled?: boolean
    editable?: boolean
    disableCursorOnly?: boolean
    node?: DataNode
}

export const Hyperlink = React.memo<HyperlinkProps>(
    ({
        contentItem,
        isKeyedHyperlink,
        itemOrder,
        inactive = false,
        disabled = false,
        editable = false,
        disableCursorOnly = false,
        node,
    }) => {
        const classNames = useTreeViewStyles()

        const { contentItemViewNodes } = useShallowSelector((state) => ({
            contentItemViewNodes: state.contentItemViewNodes,
        }))

        const isManufacturerOrBODPill = useMemo(
            () => node?.isBOD || node?.isManufacturers,
            [node?.isBOD, node?.isManufacturers]
        )
        const manufacturerPillId = useMemo(
            () => node?.contentItemView?.pillId,
            [node?.contentItemView?.pillId]
        )

        // Find the contentItemViewNode that contains this hyperlink
        const parentNode = useMemo(
            () =>
                findNodeById(contentItemViewNodes, contentItem?.id ?? '') ||
                null,
            [contentItemViewNodes, contentItem?.id]
        )

        const isCustomManufacturer = useMemo(
            () =>
                contentItem.optionItemState === 'InActive' &&
                node?.isCustomManufacturer,
            [contentItem.optionItemState, node?.isCustomManufacturer]
        )
        // Use the hyperlink selection hook
        const {
            handleSelectionOptionClick,
            isSelected,
        } = useHyperlinkSelection({
            parentNode,
        })

        // Create a conditional click handler that prevents clicks when disableCursorOnly is true
        const conditionalHandleClick = React.useCallback(
            (
                currentSelectedItem: Partial<TextContentElement>,
                currentlySelected: boolean
            ) => {
                if (disableCursorOnly) {
                    return
                }
                handleSelectionOptionClick(
                    currentSelectedItem,
                    currentlySelected
                )
            },
            [disableCursorOnly, handleSelectionOptionClick]
        )

        // Extract BP properties from child elements if they exist
        const extractBPProperties = useMemo(() => {
            // First check if BP properties exist on the contentItem itself
            if (
                contentItem.isBestPractice !== undefined ||
                contentItem.isGlobalBestPractice !== undefined
            ) {
                return {
                    isBestPractice: isManufacturerOrBODPill
                        ? node?.contentItemView?.isBestPractice
                        : contentItem.isBestPractice,
                    isGlobalBestPractice: isManufacturerOrBODPill
                        ? node?.contentItemView?.isGlobalBestPractice
                        : contentItem.isGlobalBestPractice,
                }
            }

            // Check if BP properties exist in child elements
            if (contentItem.elements && contentItem.elements.length > 0) {
                const firstElement = contentItem.elements[0]
                if (
                    firstElement.isBestPractice !== undefined ||
                    firstElement.isGlobalBestPractice !== undefined
                ) {
                    return {
                        isBestPractice: firstElement.isBestPractice,
                        isGlobalBestPractice: firstElement.isGlobalBestPractice,
                    }
                }
            }

            return {}
        }, [contentItem, isManufacturerOrBODPill, node?.contentItemView])

        return (
            <>
                {isKeyedHyperlink || manufacturerPillId === '' ? (
                    <div
                        data-testid="option-item"
                        className={
                            inactive ? classNames.inactiveText : undefined
                        }
                        style={{ display: 'inline' }}
                    >
                        <NodeOrder itemOrder={itemOrder || ''} />
                        <PlainText
                            contentItem={contentItem}
                            classes={classNames.paragraphTypography}
                            inactive={inactive}
                            editable={editable}
                        />
                    </div>
                ) : (
                    <div className="non-keyed-hyperlink-wrapper">
                        <SelectionOption
                            contentItem={{
                                ...contentItem,
                                ...extractBPProperties,
                                // For manufacturer pills, override text if available from node.contentItemView
                                ...(isManufacturerOrBODPill &&
                                node?.contentItemView?.text?.trim()
                                    ? {
                                          text: node.contentItemView.text,
                                      }
                                    : {}),
                                // Set optionItemState based on isPillSelected since API always returns null
                                optionItemState:
                                    isSelected && !isCustomManufacturer
                                        ? 'OptedIn'
                                        : null,
                            }}
                            inactive={inactive}
                            disabled={disabled}
                            editable={editable}
                            onSelectionOptionClick={conditionalHandleClick}
                            disableCursorOnly={disableCursorOnly}
                            isManufacturerOrBODPill={isManufacturerOrBODPill}
                            parentContentItemId={
                                isManufacturerOrBODPill
                                    ? node?.contentItemView?.pillId
                                    : parentNode?.dtvContentItemId ||
                                      parentNode?.dtvParentId
                            }
                        />
                    </div>
                )}
            </>
        )
    }
)
